import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, LogOut, Mail, ShieldQuestion } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { meineLinks, type MeinLink } from '../../lib/planung';

function linkText(l: MeinLink): string {
  if (l.sicht === 'alle') return 'alle Mails';
  if (l.sicht === 'sportschulen' && l.sportschulen.length) return l.sportschulen.join(', ');
  return 'Mails, zu denen wir Dich fragen';
}

/**
 * Angemeldet, aber ohne Rolle im Mailtool. Geschäftsführung und
 * Studioleitung bekommen ihre Rolle von selbst aus dem CRM — wer hier
 * landet, hat dort (noch) keine passende Rolle. Ist das Konto aber
 * Ansprechperson (z. B. ein Studioaccount), führt ein Knopf direkt zu den
 * eigenen Mails (mail_meine_links, Migration 0051).
 */
export function KeinZugang() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const links = useQuery({ queryKey: ['meine-links', user?.id ?? null], queryFn: meineLinks, retry: 1, staleTime: 60_000 });
  const eigene = links.data ?? [];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 1rem', background: 'var(--bg-primary)' }}>
      <div className="card" style={{ maxWidth: 560, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-text)', marginBottom: '1rem' }}>
          <Mail size={22} aria-hidden="true" /><strong style={{ fontSize: 'var(--fs-lg)' }}>Randori Mail</strong>
        </div>

        {eigene.length > 0 ? (
          <>
            <h1 style={{ fontSize: 'var(--fs-2xl)', margin: '0 0 0.75rem' }}>Deine Mails zum Abstimmen</h1>
            <p style={{ lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Du bist als <strong style={{ overflowWrap: 'anywhere' }}>{user?.email}</strong> angemeldet. Hier siehst Du die geplanten Mails,
              kannst sie kommentieren und Änderungen vorschlagen.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: '1rem 0 1.25rem' }}>
              {eigene.map((l) => (
                <button
                  key={l.token}
                  type="button"
                  className="btn btn-primary"
                  style={{ justifyContent: 'space-between', textAlign: 'left', minHeight: '3rem', whiteSpace: 'normal' }}
                  onClick={() => navigate(`/feedback/${l.token}`)}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <strong>Zu Deinen Mails</strong>
                    <span style={{ fontSize: 'var(--fs-sm)', opacity: 0.9, overflowWrap: 'anywhere' }}>{l.name} · {linkText(l)}</span>
                  </span>
                  <ArrowRight size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                </button>
              ))}
            </div>
            <p style={{ lineHeight: 1.6, color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
              Das Mailtool selbst (Kampagnen, Vorlagen, Versand) ist für Dein Konto nicht freigeschaltet. Fragen dazu an{' '}
              <a href="mailto:backoffice@randori-pro.de">backoffice@randori-pro.de</a>.
            </p>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 'var(--fs-2xl)', margin: '0 0 0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <ShieldQuestion size={24} aria-hidden="true" /> Noch kein Zugang zum Mailtool
            </h1>
            <p style={{ lineHeight: 1.6, color: 'var(--text-secondary)' }}>
              Du bist als <strong style={{ overflowWrap: 'anywhere' }}>{user?.email}</strong> angemeldet, hast im Mailtool aber noch keine Rolle.
            </p>
            {links.isLoading && <p className="hint" aria-live="polite">Einen Moment — wir schauen, ob Mails zum Abstimmen für Dich da sind …</p>}
            <ul style={{ lineHeight: 1.6, color: 'var(--text-secondary)', paddingLeft: '1.1rem' }}>
              <li>Geschäftsführung und Studioleitung bekommen den Zugang von selbst aus dem CRM — sobald Deine Rolle dort eingetragen ist.</li>
              <li>Studioaccounts sehen die Mails ihrer Sportschule, wenn sie mit Google angemeldet sind — sonst bitte ab- und mit Google wieder anmelden.</li>
              <li>Alle anderen schaltet ein Admin unter Einstellungen → Benutzer frei.</li>
              <li>Fragen dazu an <a href="mailto:backoffice@randori-pro.de">backoffice@randori-pro.de</a>.</li>
            </ul>
          </>
        )}

        <button type="button" className="btn btn-secondary" onClick={async () => { await logout(); navigate('/login'); }}>
          <LogOut size={16} aria-hidden="true" /> Abmelden
        </button>
      </div>
    </div>
  );
}
